import { ArrowDown, ArrowRight, ArrowUp } from 'lucide-react';
import type { CommsSentSummary } from '@ucp/web/lib';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { cn } from '../ui/cn';
import { Skeleton } from '../admin/Skeleton';
import type { SentActivityStatus } from '../../hooks/useSentActivity';

/**
 * Sent-activity stat tiles (PRO-0620). Presentational only — renders the PII-safe
 * `CommsSentSummary` aggregate as Tier-1 DS count tiles (DV-1: no charts). Counts +
 * a method split only; never a recipient identity or body. Token-first Tailwind
 * (no raw hex/px). The flag gate + fetch live in `HomeView`/`useSentActivity`.
 *
 * Each period-bounded tile also shows a previous-period trend from `summary.previous`
 * (PRO-0620 follow-up): the same-length window immediately before the selected range.
 * Direction is conveyed by an arrow AND signed text (never colour/icon alone, NF-10).
 * Colour is applied ONLY to a tile whose polarity is meaningful — `Sends with failures`
 * (`higher-worse`: down = success, up = danger). Volume/channel tiles are `neutral`
 * (arrow + text in the standard secondary colour, no green/red) so a rise is never
 * mis-signalled as "good". `In progress` is a live snapshot and carries no trend.
 */

interface SentActivityPanelProps {
  readonly summary: CommsSentSummary | undefined;
  readonly status: SentActivityStatus;
  readonly onRetry: () => void;
}

type TrendDirection = 'up' | 'down' | 'flat';
/** `neutral`: no good/bad meaning (volume/channel). `higher-worse`: failures. */
type TrendPolarity = 'neutral' | 'higher-worse';

const TREND_ICON: Record<TrendDirection, typeof ArrowUp> = {
  up: ArrowUp,
  down: ArrowDown,
  flat: ArrowRight,
};

/**
 * Pure trend descriptor. Direction + a signed, screen-reader-complete text label
 * ("+12% vs previous 30 days"). A zero previous baseline has no meaningful
 * percentage, so it degrades to "New …"; an equal value is "No change …".
 */
export function describeTrend(
  value: number,
  previousValue: number,
  rangeDays: number,
): { readonly direction: TrendDirection; readonly text: string } {
  const suffix = `vs previous ${rangeDays} day${rangeDays === 1 ? '' : 's'}`;
  const delta = value - previousValue;
  if (delta === 0) {
    return { direction: 'flat', text: `No change ${suffix}` };
  }
  const direction: TrendDirection = delta > 0 ? 'up' : 'down';
  if (previousValue === 0) {
    return { direction, text: `New ${suffix}` };
  }
  const pct = Math.round((delta / previousValue) * 100);
  return { direction, text: `${pct > 0 ? '+' : ''}${pct}% ${suffix}` };
}

/** Colour only for a meaningful polarity; neutral tiles + flat stay secondary. */
function trendColorClass(direction: TrendDirection, polarity: TrendPolarity): string {
  if (polarity === 'neutral' || direction === 'flat') {
    return 'text-text-secondary';
  }
  // higher-worse: fewer failures (down) is good, more (up) is bad.
  return direction === 'down' ? 'text-success' : 'text-danger';
}

function StatTile({
  label,
  value,
  previousValue,
  polarity = 'neutral',
  rangeDays,
}: {
  label: string;
  value: number;
  previousValue?: number | undefined;
  polarity?: TrendPolarity;
  rangeDays?: number | undefined;
}) {
  const trend =
    previousValue !== undefined && rangeDays !== undefined
      ? describeTrend(value, previousValue, rangeDays)
      : null;
  const TrendIcon = trend ? TREND_ICON[trend.direction] : null;
  return (
    <li>
      <Card className="p-4">
        <p className="m-0 text-sm text-text-secondary">{label}</p>
        <p className="m-0 mt-1 text-xl font-bold text-text-primary">{value}</p>
        {trend && TrendIcon ? (
          <p className={cn('m-0 mt-1 flex items-center gap-1 text-xs', trendColorClass(trend.direction, polarity))}>
            <TrendIcon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
            <span>{trend.text}</span>
          </p>
        ) : null}
      </Card>
    </li>
  );
}

export function SentActivityPanel({ summary, status, onRetry }: SentActivityPanelProps) {
  if (status === 'loading') {
    return <Skeleton rows={2} label="Loading sent activity" />;
  }

  if (status === 'error' || !summary) {
    return (
      <Card className="max-w-narrow p-6">
        <p role="alert" className="m-0 text-base text-text-secondary">
          Could not load sent activity just now.
        </p>
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-3">
          Try again
        </Button>
      </Card>
    );
  }

  const dayLabel = `Last ${summary.rangeDays} day${summary.rangeDays === 1 ? '' : 's'}`;
  const prev = summary.previous;

  return (
    <ul className="m-0 grid list-none grid-cols-1 gap-4 p-0 md:grid-cols-2 lg:grid-cols-3">
      <StatTile
        label={`Sends (${dayLabel})`}
        value={summary.totalSends}
        previousValue={prev?.totalSends}
        rangeDays={summary.rangeDays}
      />
      <StatTile
        label={`Recipients (${dayLabel})`}
        value={summary.totalRecipients}
        previousValue={prev?.totalRecipients}
        rangeDays={summary.rangeDays}
      />
      <StatTile label="In progress" value={summary.inProgress} />
      <StatTile
        label="Sends using email"
        value={summary.methodSplit.email}
        previousValue={prev?.methodSplit.email}
        rangeDays={summary.rangeDays}
      />
      <StatTile
        label="Sends using SMS"
        value={summary.methodSplit.sms}
        previousValue={prev?.methodSplit.sms}
        rangeDays={summary.rangeDays}
      />
      <StatTile
        label="Sends with failures"
        value={summary.failedSends}
        previousValue={prev?.failedSends}
        polarity="higher-worse"
        rangeDays={summary.rangeDays}
      />
    </ul>
  );
}
